import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';

interface SystemStats {
  total_users: number;
  total_projects: number;
  total_reports: number;
  active_users_30d: number;
  total_credits_used: number;
  avg_confidence_score: number;
  projects_pending_qa: number;
}

interface RecentActivity {
  id: number;
  type: string;
  user_email: string;
  description: string;
  timestamp: string;
}

interface SystemHealth {
  status: string;
  database: string;
  api: string;
  model_deployed: boolean;
  model_version: string;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      // Fetch all dashboard data (axios instance already includes auth token)
      const [statsRes, activityRes, healthRes] = await Promise.all([
        axios.get('/api/admin/dashboard/stats'),
        axios.get('/api/admin/dashboard/recent-activity'),
        axios.get('/api/admin/system/health')
      ]);

      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
      setSystemHealth(healthRes.data);
      setLoading(false);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load dashboard data');
      }
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registered':
        return '👤';
      case 'project_created':
        return '📊';
      case 'report_generated':
        return '📄';
      default:
        return '📌';
    }
  };

  if (loading) {
    return (
      <div className="loading-page">
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="container" style={{ paddingTop: '2rem' }}>
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="container">
          <div className="page-header-content">
            <div className="page-header-left">
              <h1 className="page-title">🛠️ Admin Dashboard</h1>
            </div>
            <div className="page-header-right">
              <button onClick={() => navigate('/admin/users')} className="btn btn-sm btn-outline">
                👥 Users
              </button>
              <button onClick={() => navigate('/admin/qa')} className="btn btn-sm btn-outline">
                ✅ QA Pipeline
              </button>
              <button onClick={() => navigate('/admin/models')} className="btn btn-sm btn-outline">
                🤖 Models
              </button>
              <button onClick={() => navigate('/dashboard')} className="btn btn-sm btn-ghost">
                ← Back
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          {/* System Health Status */}
          {systemHealth && (
            <div className={`alert ${systemHealth.status === 'healthy' ? 'alert-success' : 'alert-warning'}`} style={{ marginBottom: 'var(--spacing-6)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%', flexWrap: 'wrap', gap: 'var(--spacing-4)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <strong>System:</strong> {systemHealth.status}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <strong>Database:</strong> {systemHealth.database}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <strong>API:</strong> {systemHealth.api}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                  <strong>Model:</strong> {systemHealth.model_deployed ? `✅ ${systemHealth.model_version}` : '❌ Not deployed'}
                </div>
              </div>
            </div>
          )}

          {/* Statistics Grid */}
          {stats && (
            <div className="stats-grid" style={{ marginBottom: 'var(--spacing-8)' }}>
              <div className="stat-card">
                <div className="stat-label">Total Users</div>
                <div className="stat-value">{stats.total_users.toLocaleString()}</div>
                <div className="stat-change" style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                  {stats.active_users_30d} active (30d)
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Total Projects</div>
                <div className="stat-value">{stats.total_projects.toLocaleString()}</div>
                <div className="stat-change" style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                  {stats.total_reports} reports generated
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Credits Used</div>
                <div className="stat-value">{stats.total_credits_used.toLocaleString()}</div>
                <div className="stat-change" style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                  All time
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-label">Avg Confidence</div>
                <div className="stat-value">{(stats.avg_confidence_score * 100).toFixed(1)}%</div>
                <div className="stat-change" style={{ color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                  {stats.projects_pending_qa} pending QA
                </div>
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="page-section">
            <div className="section-header">
              <h2 className="section-title">Recent Activity</h2>
            </div>
            <div className="card">
              <div className="card-body">
                {recentActivity.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-title">No recent activity</div>
                    <div className="empty-state-description">Activity will appear here as users interact with the system</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                    {recentActivity.map((activity) => (
                      <div key={`${activity.type}-${activity.id}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-3)', padding: 'var(--spacing-3)', borderRadius: 'var(--radius-lg)', background: 'var(--color-gray-50)' }}>
                        <div style={{ fontSize: 'var(--text-xl)' }}>{getActivityIcon(activity.type)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-900)', marginBottom: 'var(--spacing-1)' }}>
                            {activity.description}
                          </div>
                          <div style={{ display: 'flex', gap: 'var(--spacing-3)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                            <span>{activity.user_email}</span>
                            <span>•</span>
                            <span>{formatDate(activity.timestamp)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="page-section">
            <div className="section-header">
              <h2 className="section-title">Quick Actions</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--spacing-4)' }}>
              <button onClick={() => navigate('/admin/users')} className="card" style={{ cursor: 'pointer', border: '1px solid var(--color-gray-200)', transition: 'all var(--transition-fast)' }}>
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-2)' }}>👥</div>
                  <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>Manage Users</div>
                </div>
              </button>
              <button onClick={() => navigate('/admin/qa')} className="card" style={{ cursor: 'pointer', border: '1px solid var(--color-gray-200)', transition: 'all var(--transition-fast)' }}>
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-2)' }}>✅</div>
                  <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>QA Reviews</div>
                </div>
              </button>
              <button onClick={() => navigate('/admin/models')} className="card" style={{ cursor: 'pointer', border: '1px solid var(--color-gray-200)', transition: 'all var(--transition-fast)' }}>
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-2)' }}>🤖</div>
                  <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>Model Versions</div>
                </div>
              </button>
              <button onClick={() => navigate('/admin/subscriptions')} className="card" style={{ cursor: 'pointer', border: '1px solid var(--color-gray-200)', transition: 'all var(--transition-fast)' }}>
                <div className="card-body" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 'var(--spacing-2)' }}>�</div>
                  <div style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-900)' }}>Subscriptions</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
